import React, { useState, useEffect, useCallback } from 'react';
import './Sidebar.css';
import api from '../services/api';
import { getModuleIconSrc, SettingsIcon } from '../assets/moduleIcons';

const CHAT_API_BASE = '/api/chat';

// Authenticated fetch wrapper - adds Bearer token to all requests
const authFetch = async (url, options = {}) => {
  const token = await api.ensureAuthenticated();
  const headers = {
    ...(options.headers || {}),
    'Authorization': `Bearer ${token}`,
  };
  return fetch(url, { ...options, headers });
};

const Sidebar = ({ onModuleSelect, currentModule, userId, deniedPages }) => {
  // Per-user page restrictions: never show a denied module in the nav (or in the
  // configure-sidebar list). Backend independently blocks the endpoints too.
  const isDenied = useCallback((id) => !!deniedPages && deniedPages.has && deniedPages.has(id), [deniedPages]);
  const [modules, setModules] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [visibleModules, setVisibleModules] = useState([]);
  const [moduleOrder, setModuleOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch sidebar configuration
  const fetchSidebarConfig = useCallback(async () => {
    try {
      setLoading(true);
      const url = userId
        ? `${CHAT_API_BASE}/sidebar-config.php?user_id=${encodeURIComponent(userId)}`
        : `${CHAT_API_BASE}/sidebar-config.php`;

      const response = await authFetch(url);
      const data = await response.json();

      if (data.success) {
        setAllModules(data.all_modules || []);
        setVisibleModules(data.preferences?.visible_modules || []);
        setModuleOrder(data.preferences?.module_order || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch sidebar config:', err);
      // Use defaults
      setAllModules(getDefaultModules());
      // Fallback matches backend sidebar-config.php $defaultVisibleModules.
      const fallback = [
        'Accounts', 'Contacts', 'Leads', 'Opportunities',
        'Project', 'AOS_Products', 'AOS_Quotes', 'AOS_Invoices',
        'jcrm_Businesses', 'jcrm_PurchaseOrders', 'jcrm_Accounting',
        'Calls', 'Meetings', 'Tasks',
      ];
      setVisibleModules(fallback);
      setModuleOrder(fallback);
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSidebarConfig();
  }, [fetchSidebarConfig]);

  // Build ordered modules list for display
  useEffect(() => {
    if (allModules.length > 0 && visibleModules.length > 0) {
      // Create ordered list of visible modules
      const orderedModules = moduleOrder
        .filter(id => visibleModules.includes(id))
        .map(id => allModules.find(m => m.id === id))
        .filter(Boolean);

      // Add any visible modules not in order
      visibleModules.forEach(id => {
        if (!moduleOrder.includes(id)) {
          const mod = allModules.find(m => m.id === id);
          if (mod) orderedModules.push(mod);
        }
      });

      setModules(orderedModules.filter(m => !isDenied(m.id)));
    }
  }, [allModules, visibleModules, moduleOrder, isDenied]);

  const getDefaultModules = () => [
    { id: 'Accounts', name: 'Accounts', icon: '🏢' },
    { id: 'Contacts', name: 'Contacts', icon: '👤' },
    { id: 'Leads', name: 'Leads', icon: '🎯' },
    { id: 'Opportunities', name: 'Opportunities', icon: '💰' },
    { id: 'Project', name: 'Projects', icon: '📊' },
    { id: 'AOS_Products', name: 'Products', icon: '📦' },
    { id: 'AOS_Quotes', name: 'Proposals', icon: '💵' },
    { id: 'AOS_Invoices', name: 'Invoices', icon: '🧾' },
    { id: 'jcrm_Businesses', name: 'Businesses', icon: '🏪' },
    { id: 'jcrm_PurchaseOrders', name: 'Purchase Orders', icon: '📋' },
    { id: 'jcrm_Accounting', name: 'Accounting', icon: '📒' },
    { id: 'Cases', name: 'Cases', icon: '📋' },
    { id: 'Calls', name: 'Calls', icon: '📞' },
    { id: 'Meetings', name: 'Meetings', icon: '📅' },
    { id: 'Tasks', name: 'Tasks', icon: '✓' },
  ];

  const savePreferences = async (newVisible, newOrder) => {
    if (!userId) return;

    setSaving(true);
    try {
      await authFetch(`${CHAT_API_BASE}/sidebar-config.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          visible_modules: newVisible,
          module_order: newOrder
        })
      });
    } catch (err) {
      console.error('Failed to save preferences:', err);
    }
    setSaving(false);
  };

  const handleVisibilityToggle = (moduleId) => {
    const newVisible = visibleModules.includes(moduleId)
      ? visibleModules.filter(id => id !== moduleId)
      : [...visibleModules, moduleId];

    setVisibleModules(newVisible);

    // Update order to include new module at end if added
    let newOrder = [...moduleOrder];
    if (!moduleOrder.includes(moduleId) && newVisible.includes(moduleId)) {
      newOrder = [...moduleOrder, moduleId];
      setModuleOrder(newOrder);
    }

    savePreferences(newVisible, newOrder);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newOrder = [...moduleOrder];
    const visibleOrder = moduleOrder.filter(id => visibleModules.includes(id));
    const draggedId = visibleOrder[draggedItem];
    const targetId = visibleOrder[index];

    // Reorder in the full order list
    const draggedFullIndex = newOrder.indexOf(draggedId);
    const targetFullIndex = newOrder.indexOf(targetId);

    newOrder.splice(draggedFullIndex, 1);
    newOrder.splice(targetFullIndex, 0, draggedId);

    setModuleOrder(newOrder);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    if (draggedItem !== null) {
      savePreferences(visibleModules, moduleOrder);
    }
    setDraggedItem(null);
  };

  const moveModule = (index, direction) => {
    const visibleOrder = moduleOrder.filter(id => visibleModules.includes(id));
    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= visibleOrder.length) return;

    const moduleId = visibleOrder[index];
    const targetId = visibleOrder[newIndex];

    const newOrder = [...moduleOrder];
    const moduleFullIndex = newOrder.indexOf(moduleId);
    const targetFullIndex = newOrder.indexOf(targetId);

    newOrder.splice(moduleFullIndex, 1);
    newOrder.splice(targetFullIndex, 0, moduleId);

    setModuleOrder(newOrder);
    savePreferences(visibleModules, newOrder);
  };

  const handleModuleClick = (module) => {
    onModuleSelect(module.id);
  };

  const getDisplayName = (module) => {
    const displayNames = {
      'AOS_Products': 'Products',
      'AOS_Product_Categories': 'Categories',
      'AOS_Quotes': 'Proposals',
      'AOS_Invoices': 'Invoices',
      'AOS_Contracts': 'Contracts',
      'AOR_Reports': 'Reports',
      'jcrm_Businesses': 'Businesses',
      'jcrm_Accounting': 'Accounting',
      'FP_events': 'Events',
      'AOS_PDF_Templates': 'PDF Templates',
      'ProspectLists': 'Target Lists',
      'Prospects': 'Targets'
    };
    return displayNames[module.id] || module.name || module.id;
  };

  return (
    <>
      <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <h2 className="sidebar-title">
            {!isCollapsed && 'Modules'}
          </h2>
          <div className="sidebar-header-buttons">
            {!isCollapsed && (
              <button
                className="sidebar-settings-btn"
                onClick={() => setShowSettings(true)}
                title="Configure Sidebar"
              >
                <img src={SettingsIcon} alt="Settings" className="module-png-icon" />
              </button>
            )}
            <button
              className="sidebar-toggle"
              onClick={() => setIsCollapsed(!isCollapsed)}
              title={isCollapsed ? 'Expand' : 'Collapse'}
            >
              {isCollapsed ? '›' : '‹'}
            </button>
          </div>
        </div>

        {loading && !isCollapsed && (
          <div className="sidebar-loading">Loading modules...</div>
        )}

        <nav className="sidebar-nav">
          {modules.map((module, index) => {
            const isActive = currentModule === module.id;

            return (
              <button
                key={module.id}
                className={`sidebar-button ${isActive ? 'active' : ''}`}
                onClick={() => handleModuleClick(module)}
                title={getDisplayName(module)}
                draggable={!isCollapsed}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
              >
                <span className="sidebar-icon">
                  {getModuleIconSrc(module.id)
                    ? <img src={getModuleIconSrc(module.id)} alt="" className="module-png-icon" />
                    : module.icon}
                </span>
                {!isCollapsed && (
                  <span className="sidebar-label">{getDisplayName(module)}</span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Settings Modal */}
      {showSettings && (
        <div className="sidebar-settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="sidebar-settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h3>Configure Sidebar Modules</h3>
              <button className="settings-close-btn" onClick={() => setShowSettings(false)}>×</button>
            </div>

            <div className="settings-modal-content">
              <p className="settings-hint">
                Check modules to show in sidebar. Use arrows or drag to reorder.
                {saving && <span className="saving-indicator"> Saving...</span>}
              </p>

              <div className="settings-modules-list">
                {moduleOrder.filter(id => visibleModules.includes(id) && !isDenied(id)).map((moduleId, index) => {
                  const module = allModules.find(m => m.id === moduleId);
                  if (!module) return null;

                  return (
                    <div
                      key={module.id}
                      className="settings-module-item visible"
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="module-drag-handle">⋮⋮</div>
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={() => handleVisibilityToggle(module.id)}
                        id={`module-${module.id}`}
                      />
                      <label htmlFor={`module-${module.id}`}>
                        <span className="module-icon">
                          {getModuleIconSrc(module.id)
                            ? <img src={getModuleIconSrc(module.id)} alt="" className="module-png-icon" />
                            : module.icon}
                        </span>
                        <span className="module-name">{getDisplayName(module)}</span>
                      </label>
                      <div className="module-order-buttons">
                        <button
                          onClick={() => moveModule(index, -1)}
                          disabled={index === 0}
                          title="Move up"
                        >↑</button>
                        <button
                          onClick={() => moveModule(index, 1)}
                          disabled={index === visibleModules.length - 1}
                          title="Move down"
                        >↓</button>
                      </div>
                    </div>
                  );
                })}

                <div className="settings-divider">
                  <span>Hidden Modules</span>
                </div>

                {allModules
                  .filter(m => !visibleModules.includes(m.id) && !isDenied(m.id))
                  .map((module) => (
                    <div key={module.id} className="settings-module-item hidden">
                      <div className="module-drag-handle invisible">⋮⋮</div>
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={() => handleVisibilityToggle(module.id)}
                        id={`module-${module.id}`}
                      />
                      <label htmlFor={`module-${module.id}`}>
                        <span className="module-icon">
                          {getModuleIconSrc(module.id)
                            ? <img src={getModuleIconSrc(module.id)} alt="" className="module-png-icon" />
                            : module.icon}
                        </span>
                        <span className="module-name">{getDisplayName(module)}</span>
                      </label>
                    </div>
                  ))}
              </div>
            </div>

            <div className="settings-modal-footer">
              <button className="settings-done-btn" onClick={() => setShowSettings(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
